<template>
  <PropertyPricesPage v-if="!hasResolveErrors" />
  <HgPageNotFound v-else :theme="getCurrentPlatformTheme()" />
</template>

<script lang="ts" setup>
import { type HgSupportedLanguage, HgPageNotFound } from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { computed, onServerPrefetch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import { getGeoUrlLanguageCandidates, getGeoUrlNameByUrl } from '@/helpers/geo/geoUrlHelper';
import { getCurrentPlatformTheme } from '@/helpers/platform';
import { getLocalizedPageRoute } from '@/router';
import useGeoStore from '@/stores/useGeoStore';
import useMapStore from '@/stores/useMapStore';
import PropertyPricesPage from '@/views/property-prices-page/PropertyPricesPage.vue';

const { locale } = useI18n();
const route = useRoute();
const router = useRouter();
const geoStore = useGeoStore();
const mapStore = useMapStore();
const { getIaziLevelByGeoType, getUrlByGeoLocation, hasResolveErrors } = storeToRefs(geoStore);

const locationParams = computed<string>(() => route.params.location as string);
const locationQuery = computed<string>(() => route.query.zipCity as string);

const fetchGeoLocationByUrlParam = async (): Promise<void> => {
  if (!locationParams.value) {
    return;
  }

  const language = locale.value as HgSupportedLanguage;
  const candidateLanguages = getGeoUrlLanguageCandidates(locationParams.value, language);
  let resolvedLanguage: HgSupportedLanguage | null = null;

  for (const urlLanguage of candidateLanguages) {
    const urlName = getGeoUrlNameByUrl({
      param: locationParams.value,
      zipQuery: locationQuery.value,
      language: urlLanguage,
    });

    await geoStore.resolveSearchLocationByUrl(urlName, urlLanguage);

    if (!geoStore.hasResolveErrors) {
      resolvedLanguage = urlLanguage;
      break;
    }
  }

  if (!resolvedLanguage) {
    return;
  }

  mapStore.setCurrentLevel(getIaziLevelByGeoType.value);

  if (resolvedLanguage !== language) {
    const localizedParams = getUrlByGeoLocation.value[language];
    await router.replace(getLocalizedPageRoute(route, language, localizedParams));
  }
};

onServerPrefetch(fetchGeoLocationByUrlParam);
</script>
